import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

/**
 * QuickSaveContext
 *
 * Manages "Quick Save Mode" state for the search results experience.
 * When enabled, saves are done with a single click to the pre-selected
 * destinations (boards and/or collections).
 *
 * State is session-based (lost on page refresh/navigation away).
 */

const QuickSaveContext = createContext();

// Empty destinations constant to avoid recreating object
const EMPTY_DESTINATIONS = {
  boardIds: [],
  collectionIds: [],
  tripCollections: [], // Array of { tripId, collectionId, tripName, collectionName }
  boardNames: [],
  collectionNames: [],
  tripCollectionNames: []
};

// Custom hook to use the quick save context
export const useQuickSave = () => {
  const context = useContext(QuickSaveContext);
  if (context === undefined) {
    throw new Error('useQuickSave must be used within a QuickSaveProvider');
  }
  return context;
};

// Quick Save Provider Component
export const QuickSaveProvider = ({ children }) => {
  // Quick save mode state
  const [isQuickSaveEnabled, setIsQuickSaveEnabled] = useState(false);

  // The destinations to quick-save to (can be boards, collections, or both)
  const [quickSaveDestinations, setQuickSaveDestinations] = useState(EMPTY_DESTINATIONS);

  // Track if user has declined quick save (so we don't prompt again on every save)
  const [hasDeclinedQuickSave, setHasDeclinedQuickSave] = useState(false);

  // Track the number of saves in this session (to know when to prompt)
  const [saveCountThisSession, setSaveCountThisSession] = useState(0);

  /**
   * Enable quick save mode with specific destinations
   */
  const enableQuickSave = useCallback((destinations) => {
    setQuickSaveDestinations({
      boardIds: destinations.boardIds || [],
      collectionIds: destinations.collectionIds || [],
      tripCollections: destinations.tripCollections || [],
      boardNames: destinations.boardNames || [],
      collectionNames: destinations.collectionNames || [],
      tripCollectionNames: destinations.tripCollectionNames || []
    });
    setIsQuickSaveEnabled(true);
    setHasDeclinedQuickSave(false);
  }, []);

  /**
   * Disable quick save mode (user clicked X or wants to change destinations)
   */
  const disableQuickSave = useCallback(() => {
    setIsQuickSaveEnabled(false);
    setQuickSaveDestinations(EMPTY_DESTINATIONS);
    // Don't set hasDeclinedQuickSave here - user may want to re-enable
  }, []);

  /**
   * User declined the quick save prompt
   */
  const declineQuickSave = useCallback(() => {
    setHasDeclinedQuickSave(true);
    setIsQuickSaveEnabled(false);
  }, []);

  /**
   * Reset declined state (allows re-prompting)
   */
  const resetDeclined = useCallback(() => {
    setHasDeclinedQuickSave(false);
  }, []);

  /**
   * Increment save count (called after each save)
   */
  const incrementSaveCount = useCallback(() => {
    setSaveCountThisSession(prev => prev + 1);
  }, []);

  /**
   * Check if we should prompt for quick save
   * Only prompt after first save and if user hasn't declined
   */
  const shouldPromptQuickSave = useCallback(() => {
    return saveCountThisSession === 1 && !isQuickSaveEnabled && !hasDeclinedQuickSave;
  }, [saveCountThisSession, isQuickSaveEnabled, hasDeclinedQuickSave]);

  /**
   * Get display name for quick save destinations
   */
  const getQuickSaveDisplayName = useCallback(() => {
    const { boardNames, collectionNames, tripCollectionNames } = quickSaveDestinations;
    const allNames = [...tripCollectionNames, ...boardNames, ...collectionNames];

    if (allNames.length === 0) return '';
    if (allNames.length === 1) return allNames[0];
    if (allNames.length === 2) return allNames.join(' & ');
    return `${allNames[0]} +${allNames.length - 1} more`;
  }, [quickSaveDestinations]);

  /**
   * Reset all state (useful when navigating away from search)
   */
  const resetQuickSave = useCallback(() => {
    setIsQuickSaveEnabled(false);
    setQuickSaveDestinations(EMPTY_DESTINATIONS);
    setHasDeclinedQuickSave(false);
    setSaveCountThisSession(0);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // State
    isQuickSaveEnabled,
    quickSaveDestinations,
    hasDeclinedQuickSave,
    saveCountThisSession,

    // Actions
    enableQuickSave,
    disableQuickSave,
    declineQuickSave,
    resetDeclined,
    incrementSaveCount,
    resetQuickSave,

    // Computed
    shouldPromptQuickSave,
    getQuickSaveDisplayName
  }), [
    isQuickSaveEnabled,
    quickSaveDestinations,
    hasDeclinedQuickSave,
    saveCountThisSession,
    enableQuickSave,
    disableQuickSave,
    declineQuickSave,
    resetDeclined,
    incrementSaveCount,
    resetQuickSave,
    shouldPromptQuickSave,
    getQuickSaveDisplayName
  ]);

  return (
    <QuickSaveContext.Provider value={value}>
      {children}
    </QuickSaveContext.Provider>
  );
};

export default QuickSaveContext;
